import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import './CookieBanner.scss';

let showBannerCallback = null;

export const setShowBannerCallback = (callback) => {
    showBannerCallback = callback;
};

export const triggerShowBanner = () => {
    if (showBannerCallback) {
        showBannerCallback();
    }
};

const CookieBanner = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setConsent] = useState({
        necessary: true,
        analytics: false,
        marketing: false,
        formsData: false
    });

    const excludedPaths = [
        '/privacy-policy-popup',
        '/404'
    ];

    const isExcludedPage = excludedPaths.some(path => location.pathname.includes(path));

    const forceShowBanner = useCallback(() => {
        setIsVisible(true);
        setShowSettings(false);
    }, []);

    useEffect(() => {
        setShowBannerCallback(forceShowBanner);
        return () => setShowBannerCallback(null);
    }, [forceShowBanner]);

    useEffect(() => {
        if (isExcludedPage) {
            setIsVisible(false);
            return;
        }

        const savedConsent = localStorage.getItem('cookieConsent');
        if (savedConsent) {
            try {
                const parsed = JSON.parse(savedConsent);
                setConsent(parsed);
                setIsVisible(false);
                applyConsent(parsed);
            } catch (e) {
                const timer = setTimeout(() => {
                    setIsVisible(true);
                }, 2500);
                return () => clearTimeout(timer);
            }
        } else {
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [isExcludedPage]);

    const saveConsent = (newConsent) => {
        localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
        setConsent(newConsent);
        setIsVisible(false);
        setShowSettings(false);

        try {
            fetch('/api/consent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    consent: newConsent,
                    timestamp: new Date().toISOString()
                })
            }).catch(err => console.error('Failed to send consent to server:', err));
        } catch (error) {
            console.error('Failed to send consent to server:', error);
        }

        applyConsent(newConsent);
    };

    const applyConsent = (newConsent) => {
        window.dataLayer = window.dataLayer || [];

        if (newConsent.analytics) {
            if (process.env.NODE_ENV === 'development') {
                console.log('✅ Analytics согласие дано');
            }

            if (typeof window.gtag === 'function') {
                window.gtag('consent', 'update', {
                    analytics_storage: 'granted'
                });
            }

            window.dataLayer.push({
                event: 'cookie_analytics_consent_given',
                analytics_consent: 'granted'
            });
        } else {
            if (process.env.NODE_ENV === 'development') {
                console.log('❌ Analytics согласие отклонено');
            }

            if (typeof window.gtag === 'function') {
                window.gtag('consent', 'update', {
                    analytics_storage: 'denied'
                });
            }
        }

        if (newConsent.marketing) {
            if (process.env.NODE_ENV === 'development') {
                console.log('✅ Marketing согласие дано');
            }

            if (typeof window.gtag === 'function') {
                window.gtag('consent', 'update', {
                    ad_storage: 'granted',
                    ad_user_data: 'granted',
                    ad_personalization: 'granted'
                });
            }

            window.dataLayer.push({
                event: 'cookie_marketing_consent_given',
                marketing_consent: 'granted'
            });
        } else {
            if (process.env.NODE_ENV === 'development') {
                console.log('❌ Marketing согласие отклонено');
            }

            if (typeof window.gtag === 'function') {
                window.gtag('consent', 'update', {
                    ad_storage: 'denied',
                    ad_user_data: 'denied',
                    ad_personalization: 'denied'
                });
            }
        }

        if (newConsent.formsData) {
            if (process.env.NODE_ENV === 'development') {
                console.log('✅ Согласие на обработку данных форм дано');
            }
        } else {
            if (process.env.NODE_ENV === 'development') {
                console.log('❌ Согласие на обработку данных форм отклонено');
            }
        }
    };

    const acceptAll = () => {
        const newConsent = {
            necessary: true,
            analytics: true,
            marketing: true,
            formsData: true
        };
        saveConsent(newConsent);
    };

    const declineAll = () => {
        const newConsent = {
            necessary: true,
            analytics: false,
            marketing: false,
            formsData: false
        };
        saveConsent(newConsent);
    };

    const saveSettings = () => {
        saveConsent(consent);
    };

    const acceptAllInSettings = () => {
        setConsent({
            necessary: true,
            analytics: true,
            marketing: true,
            formsData: true
        });
    };

    const updateConsent = (type, value) => {
        setConsent(prev => ({ ...prev, [type]: value }));
    };

    const texts = {
        ua: {
            title: 'Налаштування конфіденційності',
            description: 'Щоб зробити ваш досвід на сайті кращим, ми використовуємо cookies та збираємо персональні дані. Це допомагає нам аналізувати відвідуваність, покращувати контент і показувати більш релевантну інформацію. Ви маєте право вибрати, які категорії дозволити. Для цього натисніть «Налаштувати». Детальніше про використання даних читайте в нашій {privacyLink}.',
            descriptionSettings: 'Ви маєте право вибрати, які категорії дозволити. Налаштуйте ваші уподобання. Детальніше про використання даних читайте в нашій {privacyLink}.',
            necessary: 'Необхідні',
            necessaryDesc: 'Потрібні для роботи сайту. Не можуть бути вимкнені.',
            analytics: 'Аналітичні дані',
            analyticsDesc: 'Допомагають аналізувати відвідуваність і покращувати сайт.',
            marketing: 'Маркетингові дані',
            marketingDesc: 'Використовуються для ретаргетингу та реклами.',
            formsData: 'Обробка персональних даних',
            formsDataDesc: 'Згода на обробку персональних даних, надісланих через форми на сайті.',
            acceptAll: 'Прийняти всі',
            declineAll: 'Відхилити всі',
            saveSettings: 'Зберегти налаштування',
            customize: 'Налаштувати',
            privacyLink: 'Політиці конфіденційності',
            alwaysOn: 'Завжди ввімкнено',
            acceptAllNotice: 'Натискаючи «Прийняти всі», ви погоджуєтесь на використання всіх cookies та обробку персональних даних.',
            saveSettingsNotice: 'Натискаючи «Зберегти налаштування», ви погоджуєтесь на використання обраних категорій.'
        },
        en: {
            title: 'Privacy Settings',
            description: 'To make your experience on the site better we use cookies and collect personal data. This helps us analyze traffic, improve content and show more relevant information. You have the right to choose which categories to allow. To do this click "Customize". For more information about data usage read our {privacyLink}.',
            descriptionSettings: 'You have the right to choose which categories to allow. Customize your preferences. For more information about data usage, read our {privacyLink}.',
            necessary: 'Necessary',
            necessaryDesc: 'Required for the site to function. Cannot be disabled.',
            analytics: 'Analytics data',
            analyticsDesc: 'Help us analyze traffic and improve the site.',
            marketing: 'Marketing data',
            marketingDesc: 'Used for retargeting and advertising.',
            formsData: 'Personal data processing',
            formsDataDesc: 'Consent to the processing of personal data submitted through forms on the website.',
            acceptAll: 'Accept all',
            declineAll: 'Decline all',
            saveSettings: 'Save settings',
            customize: 'Customize',
            privacyLink: 'Privacy Policy',
            alwaysOn: 'Always on',
            acceptAllNotice: 'By clicking "Accept all", you agree to the use of all cookies and the processing of personal data.',
            saveSettingsNotice: 'By clicking "Save settings", you agree to the use of the selected categories.'
        },
        ru: {
            title: 'Настройки конфиденциальности',
            description: 'Чтобы сделать ваш опыт на сайте лучше, мы используем cookies и собираем персональные данные. Это помогает нам анализировать посещаемость, улучшать контент и показывать более релевантную информацию. Вы имеете право выбрать, какие категории разрешить. Для этого нажмите «Настроить». Подробнее об использовании данных читайте в нашей {privacyLink}.',
            descriptionSettings: 'Вы имеете право выбрать, какие категории разрешить. Настройте ваши предпочтения. Подробнее об использовании данных читайте в нашей {privacyLink}.',
            necessary: 'Необходимые',
            necessaryDesc: 'Необходимы для работы сайта. Не могут быть отключены.',
            analytics: 'Аналитические данные',
            analyticsDesc: 'Помогают анализировать посещаемость и улучшать сайт.',
            marketing: 'Маркетинговые данные',
            marketingDesc: 'Используются для ретаргетинга и рекламы.',
            formsData: 'Обработка персональных данных',
            formsDataDesc: 'Согласие на обработку персональных данных, отправленных через формы на сайте.',
            acceptAll: 'Принять все',
            declineAll: 'Отклонить все',
            saveSettings: 'Сохранить настройки',
            customize: 'Настроить',
            privacyLink: 'Политике конфиденциальности',
            alwaysOn: 'Всегда включено',
            acceptAllNotice: 'Нажимая «Принять все», вы соглашаетесь на использование всех cookies и обработку персональных данных.',
            saveSettingsNotice: 'Нажимая «Сохранить настройки», вы соглашаетесь на использование выбранных категорий.'
        },
        pl: {
            title: 'Ustawienia prywatności',
            description: 'Aby poprawić Twoje wrażenia na stronie, używamy plików cookie i zbieramy dane osobowe. Pomaga nam to analizować ruch, ulepszać treści i wyświetlać bardziej odpowiednie informacje. Masz prawo wybrać, które kategorie dozwolić. Aby to zrobić, kliknij „Dostosuj”. Więcej informacji o wykorzystaniu danych znajdziesz w naszej {privacyLink}.',
            descriptionSettings: 'Masz prawo wybrać, które kategorie dozwolić. Dostosuj swoje preferencje. Więcej informacji o wykorzystaniu danych znajdziesz w naszej {privacyLink}.',
            necessary: 'Niezbędne',
            necessaryDesc: 'Wymagane do działania strony. Nie można wyłączyć.',
            analytics: 'Dane analityczne',
            analyticsDesc: 'Pomagają analizować ruch i ulepszać stronę.',
            marketing: 'Dane marketingowe',
            marketingDesc: 'Używane do retargetingu i reklamy.',
            formsData: 'Przetwarzanie danych osobowych',
            formsDataDesc: 'Zgoda na przetwarzanie danych osobowych przesłanych przez formularze na stronie.',
            acceptAll: 'Akceptuj wszystkie',
            declineAll: 'Odrzuć wszystkie',
            saveSettings: 'Zapisz ustawienia',
            customize: 'Dostosuj',
            privacyLink: 'Polityce prywatności',
            alwaysOn: 'Zawsze włączone',
            acceptAllNotice: 'Klikając „Akceptuj wszystkie”, zgadzasz się na używanie wszystkich plików cookie i przetwarzanie danych osobowych.',
            saveSettingsNotice: 'Klikając „Zapisz ustawienia”, zgadzasz się na używanie wybranych kategorii.'
        },
        de: {
            title: 'Datenschutzeinstellungen',
            description: 'Um Ihr Erlebnis auf der Website zu verbessern, verwenden wir Cookies und sammeln personenbezogene Daten. Dies hilft uns, den Traffic zu analysieren, Inhalte zu verbessern und relevantere Informationen anzuzeigen. Sie haben das Recht zu wählen, welche Kategorien Sie zulassen möchten. Klicken Sie dazu auf „Anpassen“. Weitere Informationen zur Datennutzung finden Sie in unserer {privacyLink}.',
            descriptionSettings: 'Sie haben das Recht zu wählen, welche Kategorien Sie zulassen möchten. Passen Sie Ihre Präferenzen an. Weitere Informationen zur Datennutzung finden Sie in unserer {privacyLink}.',
            necessary: 'Notwendig',
            necessaryDesc: 'Für die Funktion der Website erforderlich. Kann nicht deaktiviert werden.',
            analytics: 'Analysedaten',
            analyticsDesc: 'Helfen uns, den Traffic zu analysieren und die Website zu verbessern.',
            marketing: 'Marketingdaten',
            marketingDesc: 'Für Retargeting und Werbung verwendet.',
            formsData: 'Verarbeitung personenbezogener Daten',
            formsDataDesc: 'Einwilligung zur Verarbeitung personenbezogener Daten, die über Formulare auf der Website übermittelt werden.',
            acceptAll: 'Alle akzeptieren',
            declineAll: 'Alle ablehnen',
            saveSettings: 'Einstellungen speichern',
            customize: 'Anpassen',
            privacyLink: 'Datenschutzerklärung',
            alwaysOn: 'Immer aktiv',
            acceptAllNotice: 'Durch Klicken auf „Alle akzeptieren“ stimmen Sie der Verwendung aller Cookies und der Verarbeitung personenbezogener Daten zu.',
            saveSettingsNotice: 'Durch Klicken auf „Einstellungen speichern“ stimmen Sie der Verwendung der ausgewählten Kategorien zu.'
        },
        es: {
            title: 'Configuración de privacidad',
            description: 'Para mejorar su experiencia en el sitio, utilizamos cookies y recopilamos datos personales. Esto nos ayuda a analizar el tráfico, mejorar el contenido y mostrar información más relevante. Usted tiene derecho a elegir qué categorías permitir. Para hacerlo, haga clic en "Personalizar". Para obtener más información sobre el uso de datos, lea nuestra {privacyLink}.',
            descriptionSettings: 'Usted tiene derecho a elegir qué categorías permitir. Personalice sus preferencias. Para obtener más información sobre el uso de datos, lea nuestra {privacyLink}.',
            necessary: 'Necesarias',
            necessaryDesc: 'Necesarias para el funcionamiento del sitio. No se pueden desactivar.',
            analytics: 'Datos analíticos',
            analyticsDesc: 'Ayudan a analizar el tráfico y mejorar el sitio.',
            marketing: 'Datos de marketing',
            marketingDesc: 'Utilizadas para retargeting y publicidad.',
            formsData: 'Procesamiento de datos personales',
            formsDataDesc: 'Consentimiento para el procesamiento de datos personales enviados a través de formularios en el sitio web.',
            acceptAll: 'Aceptar todas',
            declineAll: 'Rechazar todas',
            saveSettings: 'Guardar configuración',
            customize: 'Personalizar',
            privacyLink: 'Política de privacidad',
            alwaysOn: 'Siempre activo',
            acceptAllNotice: 'Al hacer clic en "Aceptar todas", acepta el uso de todas las cookies y el procesamiento de datos personales.',
            saveSettingsNotice: 'Al hacer clic en "Guardar configuración", acepta el uso de las categorías seleccionadas.'
        }
    };

    const t = texts[currentLang] || texts.en;

    if (!isVisible) return null;

    return (
        <div className="cookie-banner__wrapper">
            <div className="cookie-banner cookie-banner--expanded">
                <div className="cookie-banner__content">
                    <h3 className="cookie-banner__title">{t.title}</h3>

                    {!showSettings ? (
                        <p className="cookie-banner__description">
                            {t.description.split('{privacyLink}')[0]}
                            <Link
                                to={`/${currentLang}/privacy-policy-popup`}
                                className="cookie-banner__link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t.privacyLink}
                            </Link>
                            {t.description.split('{privacyLink}')[1]}
                        </p>
                    ) : (
                        <p className="cookie-banner__description">
                            {t.descriptionSettings.split('{privacyLink}')[0]}
                            <Link
                                to={`/${currentLang}/privacy-policy-popup`}
                                className="cookie-banner__link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t.privacyLink}
                            </Link>
                            {t.descriptionSettings.split('{privacyLink}')[1]}
                        </p>
                    )}

                    {showSettings ? (
                        <>
                            <div className="cookie-banner__settings">
                                <div className="cookie-banner__option cookie-banner__option--disabled">
                                    <div className="cookie-banner__option-header">
                                        <span className="cookie-banner__option-name">{t.necessary}</span>
                                        <span className="cookie-banner__option-badge">{t.alwaysOn}</span>
                                    </div>
                                    <p className="cookie-banner__option-desc">{t.necessaryDesc}</p>
                                </div>

                                <div className="cookie-banner__option">
                                    <div className="cookie-banner__option-header">
                                        <label className="cookie-banner__switch">
                                            <input
                                                type="checkbox"
                                                checked={consent.formsData}
                                                onChange={(e) => updateConsent('formsData', e.target.checked)}
                                            />
                                            <span className="cookie-banner__slider"></span>
                                        </label>
                                        <span className="cookie-banner__option-name">{t.formsData}</span>
                                    </div>
                                    <p className="cookie-banner__option-desc">{t.formsDataDesc}</p>
                                </div>

                                <div className="cookie-banner__option">
                                    <div className="cookie-banner__option-header">
                                        <label className="cookie-banner__switch">
                                            <input
                                                type="checkbox"
                                                checked={consent.analytics}
                                                onChange={(e) => updateConsent('analytics', e.target.checked)}
                                            />
                                            <span className="cookie-banner__slider"></span>
                                        </label>
                                        <span className="cookie-banner__option-name">{t.analytics}</span>
                                    </div>
                                    <p className="cookie-banner__option-desc">{t.analyticsDesc}</p>
                                </div>

                                <div className="cookie-banner__option">
                                    <div className="cookie-banner__option-header">
                                        <label className="cookie-banner__switch">
                                            <input
                                                type="checkbox"
                                                checked={consent.marketing}
                                                onChange={(e) => updateConsent('marketing', e.target.checked)}
                                            />
                                            <span className="cookie-banner__slider"></span>
                                        </label>
                                        <span className="cookie-banner__option-name">{t.marketing}</span>
                                    </div>
                                    <p className="cookie-banner__option-desc">{t.marketingDesc}</p>
                                </div>
                            </div>

                            <div className="cookie-banner__notice">
                                <p className="cookie-banner__notice-text">{t.saveSettingsNotice}</p>
                            </div>

                            <div className="cookie-banner__buttons">
                                <button className="cookie-banner__btn cookie-banner__btn--save" onClick={saveSettings}>
                                    {t.saveSettings}
                                </button>
                                <button className="cookie-banner__btn cookie-banner__btn--link" onClick={acceptAllInSettings}>
                                    {t.acceptAll}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="cookie-banner__notice">
                                <p className="cookie-banner__notice-text">{t.acceptAllNotice}</p>
                            </div>
                            <div className="cookie-banner__buttons">
                                <button className="cookie-banner__btn cookie-banner__btn--accept" onClick={acceptAll}>
                                    {t.acceptAll}
                                </button>
                                <button className="cookie-banner__btn cookie-banner__btn--decline" onClick={declineAll}>
                                    {t.declineAll}
                                </button>
                                <button className="cookie-banner__btn cookie-banner__btn--customize" onClick={() => setShowSettings(true)}>
                                    {t.customize}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div >
        </div>
    );
};

export default CookieBanner;